/**
 * `checkPool.*` — a check box's pull channel: claim the next job, keep its lease, report its
 * result, and get presigned urls for its source and artifacts. Payloads and errors live in
 * `@fm/v3/checks`.
 *
 * A box authenticates exactly like an orca runner: its bearer token resolves an
 * `orca_runners` row ({@link OrcaRunnerMiddleware}), and only a row labelled `pool=checks`
 * (`CHECK_POOL_LABEL`) may claim. The group is its own rather than part of
 * `OrcaRunnerRpcs`: a box never registers, connects to a command stream or reports
 * containers (that group's push protocol, versioned by `ORCA_RUNNER_PROTOCOL_VERSION`), so
 * the worker client stays pull-only, and the handlers live in their own router file.
 *
 * - `claim` — the next job the box can run, leased for `CHECK_LEASE_SECONDS`; a long-poll up
 *   to `waitSeconds`. `job` is absent when none was ready.
 * - `heartbeat` — renew the lease. `CheckLeaseLost` means stop the job.
 * - `complete` — the job's result; the api ends the job in `checkJobStateOf(result)`.
 * - `artifactUrl` — a presigned GET for the source or a consumed artifact, or a PUT for a
 *   produced one. Only names the job's spec lists.
 *
 * Handlers: `check-pool.router.ts` (C2).
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import {
  CheckArtifactUrlInput,
  CheckClaimInput,
  CheckClaimResult,
  CheckCompleteInput,
  CheckCompleteResult,
  CheckHeartbeatResult,
  CheckLeaseLost,
  CheckLeaseRef,
  CheckPresignedUrl,
  CheckStorageError,
  CheckWorkerRefused,
} from "@fm/v3/checks";
import { Schema } from "effect";
import { DatabaseError, ValidationError } from "../errors.ts";
import { OrcaRunnerMiddleware, OrcaRunnerUnauthorized } from "../orca-runner.tags.ts";

/**
 * @since 1.0.0
 */
export class CheckPoolRpcs extends RpcGroup.make(
  Rpc.make("checkPool.claim", {
    payload: CheckClaimInput,
    success: CheckClaimResult,
    error: Schema.Union(OrcaRunnerUnauthorized, CheckWorkerRefused, DatabaseError),
  }).middleware(OrcaRunnerMiddleware),
  Rpc.make("checkPool.heartbeat", {
    payload: CheckLeaseRef,
    success: CheckHeartbeatResult,
    error: Schema.Union(OrcaRunnerUnauthorized, CheckLeaseLost, DatabaseError),
  }).middleware(OrcaRunnerMiddleware),
  Rpc.make("checkPool.complete", {
    payload: CheckCompleteInput,
    success: CheckCompleteResult,
    error: Schema.Union(OrcaRunnerUnauthorized, CheckLeaseLost, ValidationError, DatabaseError),
  }).middleware(OrcaRunnerMiddleware),
  Rpc.make("checkPool.artifactUrl", {
    payload: CheckArtifactUrlInput,
    success: CheckPresignedUrl,
    error: Schema.Union(OrcaRunnerUnauthorized, CheckLeaseLost, ValidationError, CheckStorageError, DatabaseError),
  }).middleware(OrcaRunnerMiddleware),
) {}
